import { isEqual, noop } from "lodash";
import _ from "lodash";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import type { AccountDetails } from "@/apis/gql/types";
import { AccountDropdownInput } from "@/components/shared/form/account-dropdown-input";
import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";

enum SpecialFilterValue {
  Unknown = "unknown",
}

const formSchema = z.object({
  accountId: z.string().min(1).max(255).nullable(),
  lineItemIds: z.array(z.string().min(1).max(255)),
});

export type FormValues = z.infer<typeof formSchema>;

type Props = {
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean | string | React.ReactNode;
  fullWidth?: boolean;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  initialValues: {
    accountId: string | null;
    lineItemIds: string[];
  };
  accounts: AccountDetails[];
  allowUnknownAccount?: boolean;
  onSubmit: (value: FormValues) => Promise<void> | void;
  onError?: (error: Error) => void;
  onSuccess?: (value: string | null) => void;
  variant?: "outline" | "ghost";
};

export const AccountForm = ({
  variant,
  accounts,
  className,
  initialValues,
  onError = noop,
  onSubmit = noop,
  onSuccess = noop,
  readOnly = false,
  disabled = false,
  fullWidth = false,
  allowUnknownAccount = false,
}: Props) => {
  const { t } = useTranslation();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  });

  const { reset } = form;

  useEffect(() => {
    if (!isEqual(form.getValues().accountId, initialValues.accountId)) {
      form.reset(initialValues);
    }
  }, [initialValues.accountId]);

  const handleSubmit = async ({ lineItemIds, accountId }: FormValues) => {
    const value = accountId !== SpecialFilterValue.Unknown ? accountId : null;

    const toastId = toast.loading(t("generic_loading"));

    try {
      await onSubmit({ lineItemIds, accountId: value });

      await onSuccess(value);

      setTimeout(() => {
        toast.dismiss(toastId);
      }, 100);
    } catch (_error) {
      const error = _error as Error;
      onError(error);
      toast.error(t("generic_error"), { richColors: true, id: toastId });
      reset(initialValues, { keepIsValid: true });
    }
  };

  return (
    <Form {...form}>
      <form>
        <AccountDropdownInput
          name="accountId"
          options={accounts}
          allowUnknownAccount={allowUnknownAccount}
          fullWidth={fullWidth}
          className={className}
          readOnly={readOnly}
          disabled={disabled}
          variant={variant}
        />
        <AutoSaveForm onSubmit={handleSubmit} debounceTime={0} />
      </form>
    </Form>
  );
};
